import { format } from 'date-fns';
import { getDateFnsLocale, getLocale } from './get-locale';

/**
 * Formats a date string to DD/MM/YYYY format
 * @param dateString - ISO date string or null
 * @returns Formatted date string (DD/MM/YYYY), '-' for null/empty, or original string if invalid
 */
export function formatDate(dateString: string | null): string {
  if (!dateString) {
    return '-';
  }
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return dateString;
    }
    const day = date.getUTCDate();
    const month = date.getUTCMonth() + 1;
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return dateString;
  }
}

/**
 * Formats a date string to HH:MM format
 * @param dateString - ISO date string
 * @returns Formatted time string (HH:MM) or empty string if invalid
 */
export function formatTime(dateString: string): string {
  if (!dateString) {
    return '';
  }
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return dateString;
    }
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch {
    return dateString;
  }
}

/**
 * Formats a date string to Italian locale date format with short month name
 * @param dateString - ISO date string
 * @returns Formatted date string (e.g., "01 gen 2026")
 */
export function formatDateShortMonth(dateString: string): string {
  if (!dateString) {
    return '';
  }
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return dateString;
    }
    return date.toLocaleDateString(getLocale(), {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
}

/**
 * Formats a date string to Italian locale with short month, date and time
 * @param dateString - ISO date string
 * @returns Formatted string (e.g., "01 gen 2026, 14:30")
 */
export function formatDateWithShortMonthAndTime(dateString: string): string {
  if (!dateString) {
    return '';
  }
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return dateString;
    }
    return date.toLocaleDateString(getLocale(), {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
}

/**
 * Formats a Date object to long Italian date (e.g., "01 marzo 2026")
 * @param date - Date object
 * @returns Formatted date string
 */
export function formatDateLong(date: Date): string {
  return format(date, 'dd MMMM yyyy', { locale: getDateFnsLocale() });
}

/**
 * Formats a Date object to full Italian date with weekday (e.g., "lunedì 01 marzo 2026")
 * @param date - Date object
 * @returns Formatted date string
 */
export function formatDateFull(date: Date): string {
  return format(date, 'EEEE dd MMMM yyyy', { locale: getDateFnsLocale() });
}

/**
 * Formats a date string to compact Italian date+time (e.g., "01/03/2026 14:30")
 * @param dateString - ISO date string
 * @returns Formatted date+time string
 */
export function formatDateTimeCompact(dateString: string): string {
  if (!dateString) {
    return '';
  }
  return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: getDateFnsLocale() });
}
